// Modal overlay that hosts the career.log timeline on top of the tmux layout.
//
// Flow when opened:
//   1. Hologram boot sequence plays (RubikBootSequence) — ~2.4s of terminal
//      HUD text + scan lines + progress bar. Click skips.
//   2. GitLogTimeline mounts — a `git log --graph --decorate` rendering of
//      Thanh's career, with click-to-expand commits backed by resumeData.
//
// While the boot sequence is on screen, the timeline chunk is preloaded
// in the background so the hand-off is instant.
//
// The file name still says "RubikModal" so we don't churn the open-rubik
// action wiring in TmuxLayout — the modal is now the career.log viewer,
// the Rubik metaphor is retired.
//
// Close affordances (all three behave identically):
//   - Esc key (window-level keydown listener, only while open)
//   - Click on the dimmed backdrop (the card itself stops propagation)
//   - The "×" button in the top-right of the card

import React, { Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RubikBootSequence from './RubikBootSequence';
import type { PortfolioRepo } from './projectLoader';

const LazyTimeline = React.lazy(() => import('./GitLogTimeline'));

/** Kick the lazy chunk into the browser cache while the boot sequence runs. */
function preloadTimelineChunk(): void {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  import('./GitLogTimeline');
}

export interface RubikModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Bubbled up from GitLogTimeline when a portfolio repo row is clicked.
   *  The parent (TmuxLayout) closes this modal and mounts
   *  ProjectModalWithTabs on a clean backdrop. */
  onOpenProject?: (repo: PortfolioRepo) => void;
}

const TERMINAL_GREEN = '#3ddc84';
const TERMINAL_AMBER = '#fbbf24';
const MONO_FONT = "'JetBrains Mono', ui-monospace, monospace";

const RubikModal: React.FC<RubikModalProps> = ({ isOpen, onClose, onOpenProject }) => {
  // The parent mounts/unmounts this component per open, so initial state
  // here is also "fresh per open" — no manual reset needed.
  const [bootDone, setBootDone] = useState<boolean>(false);

  // Kick off the timeline chunk preload as soon as the modal mounts, so
  // by the time the boot sequence finishes the lazy import is already cached.
  useEffect(() => {
    preloadTimelineChunk();
  }, []);

  // Esc-to-close. Attach the listener only while the modal is open so we
  // don't intercept Esc for the rest of the app.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const content = (
    <div
      // Backdrop — clicking it closes. The card below stops propagation so
      // clicks inside the card don't trigger close.
      role="dialog"
      aria-modal="true"
      aria-label="Career log timeline"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        backdropFilter: 'blur(2px)',
        fontFamily: MONO_FONT,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(960px, 94vw)',
          height: 'min(780px, 88vh)',
          background: '#0a0f0d',
          border: `1px solid ${TERMINAL_GREEN}`,
          borderRadius: '6px',
          boxShadow: '0 0 24px rgba(61, 220, 132, 0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* tmux-style title bar — path label + a hint that updates with phase. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 10px',
            borderBottom: '1px solid #1f2937',
            background: '#0d1411',
            color: TERMINAL_GREEN,
            fontSize: '12px',
            letterSpacing: '0.02em',
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#6b7280' }}>
            <span>~/career/</span>
            <span style={{ color: TERMINAL_AMBER }}>career.log</span>
            <span style={{ marginLeft: 10, color: '#6b7280', fontSize: '11px' }}>
              {bootDone
                ? 'click a commit to expand · scroll for older entries'
                : 'rebuilding career.log…'}
            </span>
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close career log"
            title="Close (Esc)"
            style={{
              background: 'transparent',
              border: '1px solid transparent',
              color: TERMINAL_GREEN,
              cursor: 'pointer',
              fontFamily: MONO_FONT,
              fontSize: '14px',
              lineHeight: 1,
              padding: '2px 8px',
              borderRadius: '3px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = TERMINAL_GREEN;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
            }}
          >
            &times;
          </button>
        </div>

        {/* Stage area — boot HUD first, then the timeline. */}
        <div style={{ flex: 1, position: 'relative', minHeight: 0, background: '#0a0f0d' }}>
          {!bootDone ? (
            <RubikBootSequence onDone={() => setBootDone(true)} />
          ) : (
            <Suspense
              fallback={
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: TERMINAL_GREEN,
                    fontFamily: MONO_FONT,
                    fontSize: '13px',
                    letterSpacing: '0.04em',
                  }}
                >
                  <span>reading reflog&hellip;</span>
                </div>
              }
            >
              <LazyTimeline forceDarkMode={true} onOpenProject={onOpenProject} />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default RubikModal;
